import { BrowserModule } from '@angular/platform-browser';
import {
  APP_INITIALIZER,
  LOCALE_ID,
  MissingTranslationStrategy,
  NgModule,
  TRANSLATIONS,
  TRANSLATIONS_FORMAT
} from '@angular/core';
import { MaintenanceComponent } from '@ui/maintenance/maintenance.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import {
  MatCardModule,
  MatDatepickerModule,
  MatDialogModule,
  MatIconModule,
  MatPaginatorIntl,
  MatSnackBarModule
} from '@angular/material';

import { CommonModule, registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxMaskModule } from 'ngx-mask';

import { environment } from '../environments/environment';

import { I18n, MISSING_TRANSLATION_STRATEGY } from '@ngx-translate/i18n-polyfill';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';

import { ErrorInterceptor, JwtInterceptor, SessionService } from '@core/auth';
import { MatPaginatorIntlRu } from '@core/services';
import { ApiModule, Configuration } from '@core/api';
import { UnexpectedErrorComponent, UiErrorsModule } from '@ui/ui-errors';
import { PageNotFoundModule } from '@ui/page-not-found';
import { BottomPopupModule } from '@ui/bottom-popup/bottom-popup.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ToastComponent, ToastModule } from '@ui/toast';

import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import { ConfirmationDialogComponent, ConfirmationDialogModule } from '@ui/confirmation-dialog';
import { ConfirmationDialogService } from '@ui/confirmation-dialog';
import { ModalPersonalDataComponent } from '@accounts/user/components/modal-personal-data/modal-personal-data.component';
import { UserModule } from '@accounts/user';
import {ModalConfirmationCodeComponent} from "@accounts/user/components/modal-confirmation-code/modal-confirmation-code.component";
import { ServiceWorkerModule } from '@angular/service-worker';
import { PushNotificationModule } from '@ui/push-notification/push-notification.module';
import { PushNotificationModalComponent } from '@ui/push-notification/push-notification-modal.component';
import { EmptyComponent } from '@core/components/components/empty-component/empty-component';
registerLocaleData(localeRu, 'ru', localeRuExtra);

const NgxPermissionsProvider = {
  provide: APP_INITIALIZER,
  useFactory: (sessionService: SessionService) => () => {
    return sessionService.init();
  },
  deps: [SessionService],
  multi: true
};

// use the require method provided by webpack
declare const require;

export function ApiConfiguration(): Configuration {
  return new Configuration({
    basePath: environment.apiBasePath,
    withCredentials: true
  });
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    MaintenanceComponent,
    EmptyComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    UiErrorsModule,
    UserModule,
    BrowserAnimationsModule,
    MatCardModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,

    PageNotFoundModule,
    ToastModule,
    ConfirmationDialogModule,
    PushNotificationModule,

    NgxPermissionsModule.forRoot(),
    NgxMaskModule.forRoot(),
    Angular2PromiseButtonModule.forRoot(),
    ApiModule.forRoot(ApiConfiguration),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    BottomPopupModule
  ],
  providers: [
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},

    { provide: TRANSLATIONS, useValue: require(`raw-loader!../locale/messages.ru.xlf`) },
    { provide: TRANSLATIONS_FORMAT, useValue: 'xlf' },
    { provide: MISSING_TRANSLATION_STRATEGY, useValue: MissingTranslationStrategy.Ignore },

    { provide: LOCALE_ID, useValue: 'ru' },

    I18n,

    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },

    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlRu },

    ConfirmationDialogService,

    SessionService,
    NgxPermissionsProvider
  ],
  entryComponents: [
    UnexpectedErrorComponent,
    ConfirmationDialogComponent,
    ModalPersonalDataComponent,
    ModalConfirmationCodeComponent,
    ToastComponent,
    PushNotificationModalComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
